import moment from "moment-timezone";
import { Moment } from "moment-timezone";
import CandidateModel from "../api/models/CandidateModel";
import { SlotStates } from "../api/models/ScheduleSlotModel";
import SessionModel from "../api/models/SessionModel";
import StudentModel from "../api/models/StudentModel";
import ApiDateParser from "../helpers/parsers/apiDateParser";
import { composeStartTimeAndDate } from "../components/sessions/helper";

const DAYS_OF_THE_WEEK: { [day: string]: number } = {
  sunday: 1,
  monday: 2,
  tuesday: 3,
  wednesday: 4,
  thursday: 5,
  friday: 6,
  saturday: 7,
};

type ApiPayloadSlot = {
  start_time: string;
  end_time: string;
  day_of_the_week: string;
  schedule_slot_id?: number;
  date?: Moment;
};

export default class CalendarScheduleSlotModel {
  startTime: Moment;
  endTime: Moment;
  date?: Moment;
  student?: StudentModel | CandidateModel;
  state?: SlotStates;
  dayOfWeek: number;
  dayOfWeekName: string;
  id: number;
  Uuid: string;
  calendarId: string;
  sessionNumber?: number | string;
  scheduleSlotType?: string;
  sessionId?: number;
  sessionStatus?: string;
  status?: string;

  constructor(arg: {
    startTime: Moment;
    endTime: Moment;
    dayOfWeek: string;
    id: number;
    student?: StudentModel | CandidateModel;
    state?: SlotStates;
    date?: Moment;
    sessionNumber?: number | string;
    scheduleSlotType?: string;
    sessionId?: number;
    sessionStatus?: string;
  }) {
    this.startTime = arg.startTime;
    this.endTime = arg.endTime;
    this.student = arg.student;
    this.state = arg.state;
    this.dayOfWeek = DAYS_OF_THE_WEEK[arg.dayOfWeek];
    this.date = arg.date;
    this.sessionId = arg.sessionId;
    this.scheduleSlotType = arg.scheduleSlotType ?? "recitation";
    this.dayOfWeekName = arg.dayOfWeek;
    this.status = arg.sessionStatus;
    this.sessionStatus = arg.sessionStatus;
    this.sessionNumber = arg.sessionNumber ?? "";
    this.id = arg.id;
    this.Uuid = `${this.id}_${this.startTime.unix()}_${this.endTime.unix()}`;
    this.calendarId = `${
      arg.dayOfWeek
    }_${this.startTime.unix()}_${this.endTime.unix()}`;
  }

  startDate(referenceDate: Moment): Moment {
    return referenceDate
      .clone()
      .set({
        hours: this.startTime.hours(),
        minutes: this.startTime.minutes(),
        seconds: this.startTime.seconds(),
      })
      .day(this.dayOfWeek - 1);
  }

  endDate(referenceDate: Moment): Moment {
    return referenceDate
      .clone()
      .set({
        hours: this.endTime.hours(),
        minutes: this.endTime.minutes(),
        seconds: this.endTime.seconds(),
      })
      .day(this.dayOfWeek - 1);
  }

  isDone(referenceDate: Moment): boolean {
    let refEndTime = this.endDate(referenceDate);
    let now = moment();
    return refEndTime.clone().subtract(15, "minutes").isBefore(now);
  }

  isInProgress(referenceDate: Moment): boolean {
    let refStartTime = this.startDate(referenceDate);
    let refEndTime = this.endDate(referenceDate);
    let now = moment();
    return now.isBetween(
      refStartTime.clone().subtract(15, "minutes"),
      refEndTime.clone().subtract(15, "minutes")
    );
  }

  isNotStarted(referenceDate: Moment): boolean {
    return !this.isDone(referenceDate) && !this.isInProgress(referenceDate);
  }

  modifyStateByDate(referenceDate: Moment) {
    if (this.state === SlotStates.canceled) {
      this.state = SlotStates.canceled;
    } else if (this.state === SlotStates.canceled_by_student) {
      this.state = SlotStates.canceled_by_student;
    } else if (this.state === SlotStates.canceled_by_tutor) {
      this.state = SlotStates.canceled_by_tutor;
    } else if (this.state === SlotStates.disconnected) {
      this.state = SlotStates.disconnected;
    } else if (this.state === SlotStates.disconnected_by_system) {
      this.state = SlotStates.disconnected_by_system;
    } else if (this.state === SlotStates.tutor_absence) {
      this.state = SlotStates.tutor_absence;
    } else if (this.state === SlotStates.student_absence) {
      this.state = SlotStates.student_absence;
    } else if (this.isDone(referenceDate)) {
      this.state = SlotStates.done;
    } else if (this.isInProgress(referenceDate)) {
      this.state = SlotStates.in_progress;
    } else {
      this.state = SlotStates.not_started;
    }
  }

  toSessionModel(referenceDate: Moment): SessionModel {
    const sessionApi = {
      id: this.Uuid,
      slot_id: this.id,
      date: ApiDateParser.toApiDate(this.startDate(referenceDate)),
      start_time: ApiDateParser.toApiDate(this.startTime),
      end_time: ApiDateParser.toApiDate(this.endTime),
      reported_status: slotStateToSessionState(this.state!),
      session_number: this.sessionNumber,
      status: this?.sessionStatus ?? "unknown",
    };
    const session = SessionModel.fromApiResponse(sessionApi);
    session["student"] = this.student;
    return session;
  }

  get isCandidateSlot() {
    return this.student instanceof CandidateModel;
  }
  get canBeCanceled(): boolean {
    /**
     * We give the ability to the candidate to cancel a session only if we are more than 60 minutes(1 hour) before the session start time
     */
    const TIME_BEFORE_START = 60;
    const now = moment();

    const startTime = composeStartTimeAndDate(this.startTime!, this.date!);

    return (
      this.state === SlotStates.not_started &&
      startTime?.diff(now, "minutes") > TIME_BEFORE_START
    );
  }

  toApiPayload(): ApiPayloadSlot {
    return {
      start_time: ApiDateParser.toNormalizedApiDate(moment(this.startTime)),
      end_time: ApiDateParser.toNormalizedApiDate(moment(this.endTime)),
      day_of_the_week: this.dayOfWeekName,
      schedule_slot_id: this.id,
      date: this.date,
    };
  }
}

const slotStateToSessionState = (slotState: SlotStates) => {
  switch (slotState) {
    case SlotStates.canceled:
      return "canceled";
    case SlotStates.not_started:
      return "scheduled";
    case SlotStates.upcoming:
    case SlotStates.in_progress:
      return "coming";
    case SlotStates.done:
      return "done";
  }
};
